<template>
  <div class="overflow-hidden transition-colors bg-white" :class="[avatarStyling, avatarSize]">
    <img v-if="image && props.icon"
         :src="props.icon"
         alt=""
         width="100%"
         height="100%">
    <icon-bounding-box v-else-if="computedIcon != null" :bounding-box="16" :icon="computedIcon"/>
    <slot/>
  </div>
</template>

<script setup lang="ts">
import { iconProp, useIconProp } from "~/components/global/utils/iconPropUtil";
import type { PropType } from 'vue'
const props = defineProps({
  ...iconProp,

  image: {
    type: Boolean,
    default: true,
    required: false
  },

  border: {
    type: Boolean,
    default: true
  },

  size: {
    type: String,
    default: "md"
  },

  aspect: {
    type: String as PropType<"square" | "round">,
    default: "round",
    required: false
  }
});

const { computedIcon } = useIconProp(props);

const avatarStyling = computed<string[]>(() => {
  const classes = [];
  if (props.border) {
    classes.push("border border-paper-border");
  } else {
    classes.push("border-none");
  }

  switch (props.aspect) {
    case "square":
      classes.push("aspect-1");
      break;
    case "round":
      classes.push("rounded-full");
      break;
  }

  // When it's not an image, center everything.
  if (!props.image) {
    classes.push("flex items-center justify-center");
  }
  return classes;
});

const avatarSize = computed(() => {
  switch (props.size) {
    case "xs":
      return "w-[20px] h-[20px] min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px]";
    case "sm":
      return "w-[28px] h-[28px] min-w-[28px] max-w-[28px] min-h-[28px] max-h-[28px]";
    case "md":
      return "w-[38px] h-[38px] min-w-[38px] max-w-[38px] min-h-[38px] max-h-[38px]";
    case "md-+":
      return "w-[44px] h-[44px] min-w-[44px] max-w-[44px] min-h-[44px] max-h-[44px]";
    case "lg":
      return "w-[48px] h-[48px] min-w-[48px] max-w-[48px] min-h-[48px] max-h-[48px]";
    case "xl":
      return "w-[58px] h-[58px] min-w-[58px] max-w-[58px] min-h-[58px] max-h-[58px] border-1";
    case "2xl":
      return "w-[62px] h-[62px] min-w-[62px] max-w-[62px] min-h-[62px] max-h-[62px] border-1";
    case "4xl":
      return "w-[84px] h-[84px] min-w-[84px] max-w-[84px] min-h-[84px] max-h-[84px] border-1";
    default:
      return "w-[38px] h-[38px] min-w-[38px] max-w-[38px] min-h-[38px] max-h-[38px]";
  }
});
</script>